import { useEffect, useState } from "react";
import { Seo } from "@/lib/seo";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import TemplateCard from "@/components/TemplateCard";
import { TopFosterPreview } from "@/components/TopFosterPreview";
import {
  ArrowRight, ArrowUpRight, Star, ShieldCheck, Quote, Sparkles,
  MessageCircleQuestion, Fingerprint, Map, Wrench, TrendingUp,
  Search, Zap, RefreshCw, Users, Rocket, Compass, Building2, LayoutTemplate, ChevronDown, Landmark, Bot, Lock, Phone,
} from "lucide-react";

// The Top Foster guided journey — the engine behind ATF WebWorks (BTaaS).
const journey = [
  { icon: MessageCircleQuestion, title: "Answer a few questions", desc: "No forms to dread. Just tell Top Foster where you're headed." },
  { icon: Fingerprint, title: "Build your Business Profile", desc: "Top Foster learns your mission, audience and goals — once." },
  { icon: Map, title: "Get your growth roadmap", desc: "A clear plan: what to build, in what order, and why." },
  { icon: Wrench, title: "We build it", desc: "Ask Top Foster designs and launches your website & tools." },
  { icon: TrendingUp, title: "We keep improving", desc: "Ongoing care and AI upgrades that compound over time." },
];

// "Where are you today?" — stage-based routing into the guided path.
const stages = [
  { num: "01", icon: Compass, stage: "Just starting out", desc: "You have an idea but no site yet. Start free and let Top Foster map your first move.", cta: "Get the Starter Kit", to: "/starter-kit", testid: "stage-starting" },
  { num: "02", icon: Rocket, stage: "Ready to grow", desc: "You're up and running but need more leads and a presence you're proud of.", cta: "Build with us", to: "/start", testid: "stage-growing" },
  { num: "03", icon: Building2, stage: "Established & scaling", desc: "You're ready to scale — grow with marketing, SEO, new pages and automation, plus a partner to run and maintain everything you've built.", cta: "Explore growth & care", to: "/marketplace", testid: "stage-scaling" },
];

// Outcome-led value props (what you GET), not features.
const outcomes = [
  { icon: Search, title: "Get found & get leads", desc: "SEO, conversion-focused design and lead capture built into every build." },
  { icon: Zap, title: "Stop the busywork", desc: "AI-accelerated copy, proposals and workflows so you move faster." },
  { icon: RefreshCw, title: "Keep growing after launch", desc: "Hosting, care and improvements that compound month after month." },
  { icon: Users, title: "One team, start to finish", desc: "From first inquiry to launch and beyond — handled by Ask Top Foster." },
];

const why = [
  { icon: ShieldCheck, title: "Premium & Original", desc: "Every design is handcrafted by Ask Top Foster — never a bought template kit." },
  { icon: Rocket, title: "Fast, Focused Delivery", desc: "A clear pipeline from inquiry to launch. You always know where things stand." },
  { icon: Users, title: "Real Partnership", desc: "Dedicated support, honest guidance, and a client portal built for clarity." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/templates", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash !== "#faq") return;
    // Fires on hash change AND re-fires as data loads (to correct position) — and still works if data is empty.
    const t = setTimeout(() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth", block: "start" }), 450);
    return () => clearTimeout(t);
  }, [location.hash, featured, testimonials]);

  return (
    <div>
      <Seo
        title="ATF WebWorks | AI Business Plans, Websites & Launch Support"
        description="Get a complete AI-powered business plan, a professional website, branding, hosting and SEO — everything to plan, launch and grow your business, done with you. Start free."
        path="/"
      />
      {/* HERO — outcome-led, asymmetric, Top Foster visual on the right */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden grain bg-navy">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 85% 15%, rgba(212,175,55,0.16), transparent 42%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 0% 100%, rgba(212,175,55,0.06), transparent 45%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 w-full grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-7">
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-6">
              <span className="h-px w-10 bg-gold" /> AI-Powered Business Growth Platform
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-white leading-[1.05]">
              Build a business, <span className="text-gold">not just a website.</span>
            </h1>
            <p className="text-lg text-gray-300 font-light mt-8 max-w-xl leading-relaxed">
              <span className="text-white">ATF WebWorks is an AI-powered business growth platform</span> that helps
              entrepreneurs and organizations plan, build, launch, and grow their business — all in one place.
            </p>
            <p className="flex items-center gap-2 text-sm text-gray-400 mt-4" data-testid="hero-veteran-founded">
              <ShieldCheck size={15} className="text-gold shrink-0" /> Veteran-founded • built for every entrepreneur
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/start" data-testid="hero-start-btn" className="bg-gold text-navy px-8 py-4 font-semibold hover:bg-gold-light transition-colors flex items-center gap-2 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy">
                Start Your Free Business Assessment <ArrowRight size={18} />
              </Link>
              <Link to="/marketplace" data-testid="hero-marketplace-btn" className="border border-white/25 text-white px-8 py-4 font-medium hover:bg-white/5 transition-colors">
                See our work
              </Link>
            </div>
            <p className="text-sm text-gray-400 mt-5 max-w-xl" data-testid="hero-care-nudge">
              Every build can include{" "}
              <Link to="/marketplace#care-plans" className="text-gold hover:underline">Hosting &amp; Care from $49/mo</Link>
              {" "}— we plan it, build it, launch it, maintain it, secure it, and help it grow.
            </p>
          </motion.div>

          {/* Top Foster journey visual */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-5">
            <div className="relative glass border border-gold/25 p-6 md:p-8" data-testid="hero-topfoster-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold">
                  <Sparkles size={20} />
                </div>
                <div>
                  <div className="text-white font-heading text-lg leading-none">Top Foster</div>
                  <div className="text-gold text-[11px] uppercase tracking-[0.2em] mt-1">Your AI Business Strategist</div>
                </div>
              </div>
              <div className="space-y-3">
                {journey.map((s, i) => (
                  <motion.div key={s.title} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.12 }}
                    className="flex items-center gap-3 bg-white/[0.03] border border-white/8 px-3.5 py-2.5">
                    <span className="w-6 h-6 shrink-0 rounded-full bg-gold text-navy text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <s.icon size={16} className="text-gold shrink-0" />
                    <span className="text-sm text-gray-200">{s.title}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CHOOSE YOUR PATH — 3 clear doors so a first-timer knows where to click */}
      <section className="border-y border-white/10 bg-navy-800/40" data-testid="plan-build-grow">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4">
              <Compass size={15} /> New here? Start here
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">Pick the one that sounds like you</h2>
            <p className="text-gray-400 mt-3 max-w-xl mx-auto">Three simple ways to begin — choose your starting point and we'll guide the rest.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
          {[
            { key: "plan", icon: Landmark, kicker: "I have an idea", title: "Help me plan my business", price: "Free to start", desc: "Not sure where to begin? Answer a few simple questions and get a business plan and a clear roadmap — free to build.", to: "/start", cta: "Start free" },
            { key: "build", icon: LayoutTemplate, kicker: "I'm ready to build", title: "Build my website for me", price: "from $997", desc: "A custom, conversion-focused site — designed, built and launched for your business by our team.", to: "/services", cta: "See how it works", secondary: { label: "Or book a call first", to: "/book" } },
            { key: "grow", icon: Bot, kicker: "I want to grow", title: "Grow what I already have", price: "Plans from $49/mo", desc: "Marketing, SEO, a 24/7 AI concierge, hosting and ongoing care that compound over time.", to: "/grow", cta: "Explore growth" },
          ].map((c, i) => (
            <motion.div key={c.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}>
              <div className="group flex flex-col h-full border border-white/10 bg-navy-800 p-7 hover:border-gold/50 transition-colors">
                <Link to={c.to} data-testid={`pbg-${c.key}`} className="flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-colors"><c.icon size={20} /></div>
                    <span className="text-[11px] uppercase tracking-[0.25em] text-gold">{c.kicker}</span>
                  </div>
                  <h3 className="font-heading text-xl text-white">{c.title}</h3>
                  <div className="text-gold font-heading text-lg mt-1">{c.price}</div>
                  <p className="text-gray-400 text-sm mt-3 leading-relaxed flex-1">{c.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm text-white mt-5 group-hover:gap-2.5 transition-all">{c.cta} <ArrowRight size={15} className="text-gold" /></span>
                </Link>
                {c.secondary && (
                  <Link to={c.secondary.to} data-testid={`pbg-${c.key}-secondary`}
                    className="mt-4 pt-4 border-t border-white/10 inline-flex items-center gap-2 text-sm text-gray-300 hover:text-gold transition-colors">
                    <Phone size={14} className="text-gold" /> {c.secondary.label}
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
          </div>
        </div>
      </section>

      {/* MEET TOP FOSTER — the journey explained */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="meet-top-foster">
        <SectionHead label="Your AI Business Strategist" title="Meet Top Foster" center />
        <p className="text-gray-300 text-lg font-light leading-relaxed max-w-2xl mx-auto text-center mt-6">
          Most agencies hand you a website and disappear. Top Foster understands your business first,
          then guides everything that follows — so what we build actually moves your mission forward.
        </p>
        <div className="grid md:grid-cols-5 gap-5 mt-16" data-testid="journey-steps">
          {journey.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative glass p-6 h-full hover:border-gold/40 transition-colors">
              <div className="text-gold/40 font-mono text-xs mb-4">STEP {i + 1}</div>
              <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold mb-4">
                <s.icon size={20} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-white mb-2 leading-snug">{s.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/start" data-testid="topfoster-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-9 py-4 font-semibold hover:bg-gold-light transition-colors">
            Start free with Top Foster <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* SEE TOP FOSTER IN ACTION — 60-second interactive preview */}
      <div className="bg-navy-800/40 border-y border-white/5">
        <TopFosterPreview />
      </div>

      {/* WHERE ARE YOU TODAY? — stage selector */}
      <section className="py-24 bg-navy-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead label="Start where you are" title="Where are you today?" />
          <div className="grid md:grid-cols-3 gap-6 mt-14" data-testid="stage-cards">
            {stages.map((p, i) => (
              <motion.div key={p.stage} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}>
                <Link to={p.to} data-testid={p.testid}
                  className="group relative block h-full overflow-hidden bg-navy-800 border border-white/10 p-8 hover:border-gold/60 transition-all duration-300 hover:-translate-y-1.5">
                  <div className="absolute top-6 right-6 font-mono text-sm text-white/15 group-hover:text-gold/40 transition-colors">{p.num}</div>
                  <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="w-14 h-14 border border-gold/40 flex items-center justify-center text-gold mb-7 group-hover:bg-gold group-hover:text-navy transition-colors duration-300">
                    <p.icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl text-white mb-3 group-hover:text-gold transition-colors">{p.stage}</h3>
                  <p className="text-gray-400 leading-relaxed mb-8 min-h-[72px]">{p.desc}</p>
                  <span className="inline-flex items-center gap-2 text-gold text-sm font-medium group-hover:gap-3 transition-all">
                    {p.cta} <ArrowRight size={15} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOMES — what you get */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <SectionHead label="The outcomes" title="What you actually get" />
        <p className="text-gray-300 text-lg font-light leading-relaxed max-w-3xl mt-6" data-testid="outcomes-statement">
          A website is the tool — growth is the point. Everything we build is aimed at results you can feel in your business.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16" data-testid="outcome-cards">
          {outcomes.map((w, i) => (
            <motion.div key={w.title} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group glass p-8 h-full hover:border-gold/40 transition-colors">
              <div className="w-12 h-12 border border-gold/40 flex items-center justify-center text-gold mb-6 group-hover:scale-110 transition-transform duration-300">
                <w.icon size={22} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl text-white mb-3 leading-snug">{w.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{w.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED DESIGNS — real proof */}
      <section className="py-24 bg-navy-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <SectionHead label="Real work, not mockups" title="Featured website designs" />
            <Link to="/marketplace" className="text-gold flex items-center gap-2 hover:gap-3 transition-all" data-testid="view-all-templates">
              View all our work <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {featured.map((t, i) => <TemplateCard key={t.id} template={t} index={i} />)}
          </div>
        </div>
      </section>

      {/* RECURRING RELATIONSHIP — we don't disappear at launch */}
      <section className="py-24 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          data-testid="recurring-band" className="relative overflow-hidden bg-gradient-to-br from-navy-700 to-navy-800 border border-gold/20 p-10 md:p-14 grain">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5">
                <RefreshCw size={15} /> After launch
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight leading-tight">
                We don't disappear at launch.
              </h2>
              <p className="text-gray-300 text-lg font-light leading-relaxed mt-5 max-w-2xl">
                Your website is the beginning, not the finish line. With <span className="text-white">Hosting &amp; Care from $49/mo</span>,
                we keep it secure, current and growing — and switch on AI capabilities like lead intake as you're ready.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/marketplace#care-plans" data-testid="recurring-cta"
                className="inline-flex items-center gap-2 bg-gold text-navy px-9 py-4 font-semibold hover:bg-gold-light transition-colors">
                See Hosting &amp; Care plans <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* WHY CHOOSE US — real proof image (featured design), no stock team photo */}
      <section className="pb-24 md:pb-32 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            {featured[0]?.preview_image ? (
              <Link to={`/template/${featured[0].slug || featured[0].id}`} data-testid="why-proof-image" className="block group">
                <img src={featured[0].preview_image} alt={featured[0].title} className="w-full h-[440px] object-cover border border-white/10" />
                <div className="absolute -bottom-6 -right-6 bg-gold text-navy p-6 hidden md:block">
                  <div className="font-heading text-2xl leading-none">Ask Top Foster</div>
                  <div className="text-xs uppercase tracking-[0.2em] mt-1">Design · Build · Grow</div>
                </div>
                <span className="absolute top-4 left-4 flex items-center gap-1.5 bg-navy/80 backdrop-blur text-gold text-xs px-3 py-1.5 border border-gold/30">
                  <LayoutTemplate size={13} /> Real ATF build — {featured[0].title}
                </span>
              </Link>
            ) : (
              <div className="w-full h-[440px] bg-navy-800 border border-white/10 flex items-center justify-center text-gray-600">Featured work loading…</div>
            )}
          </div>
          <div className="lg:col-span-6">
            <SectionHead label="Why choose us" title="A studio obsessed with quality" />
            <div className="space-y-8 mt-10">
              {why.map((w) => (
                <div key={w.title} className="flex gap-5">
                  <div className="w-12 h-12 shrink-0 border border-gold/40 flex items-center justify-center text-gold">
                    <w.icon size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl text-white mb-1">{w.title}</h4>
                    <p className="text-gray-400 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/services" data-testid="why-services-link" className="inline-flex items-center gap-2 text-gold text-sm font-medium mt-10 hover:gap-3 transition-all">
              Explore services &amp; packages <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — hidden until real client testimonials exist (no placeholder/seeded quotes) */}
      {false && testimonials.length > 0 && (
        <section className="py-24 bg-navy-800/40 border-y border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <SectionHead label="Testimonials" title="Trusted by leaders & organizations" center />
            <p className="text-gray-300 text-lg font-light leading-relaxed max-w-2xl mx-auto text-center mt-6" data-testid="testimonials-outcome-line">
              The organizations we serve don't just get a website—they get momentum for their mission.
            </p>
            <div className="grid md:grid-cols-2 gap-8 mt-16">
              {testimonials.map((t, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 2) * 0.1 }} className="glass p-8">
                  <Quote className="text-gold mb-4" size={28} />
                  <p className="text-gray-200 text-lg leading-relaxed font-light mb-6">"{t.quote}"</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">{t.name}</div>
                      <div className="text-gray-500 text-sm">{t.org}</div>
                    </div>
                    <div className="flex gap-1">{Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} size={15} className="fill-gold text-gold" />)}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <FaqSection />

      {/* VETERAN FOUNDED — mission driven founder story + factual trust signals */}
      <section className="py-24 bg-navy-800/40 border-y border-white/10" data-testid="veteran-founded-story">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-6">
            <ShieldCheck size={16} /> Our story
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mb-6">Veteran Founded. Mission Driven.</h2>
          <p className="text-gray-300 text-lg leading-relaxed max-w-3xl mx-auto">
            After 26 years of military service, I founded ATF WebWorks to help small businesses, entrepreneurs,
            nonprofits, churches, veterans, and organizations turn ideas into thriving, sustainable businesses.
            The mission is simple: treat your business like it's my own, tell you the honest truth, and stay with
            you long after launch.
          </p>
          <p className="text-gold font-heading text-lg mt-6">— Top Foster, Founder</p>

          {/* Factual trust signals — every claim here is true and verifiable. */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14 text-left" data-testid="trust-signals">
            {[
              { icon: ShieldCheck, title: "Veteran-founded", desc: "Built on 26 years of service and discipline." },
              { icon: Fingerprint, title: "You own it 100%", desc: "Everything we build for you is yours to keep." },
              { icon: Lock, title: "Secure payments", desc: "Encrypted checkout powered by Stripe." },
              { icon: RefreshCw, title: "We stay after launch", desc: "Ongoing care, updates and support as you grow." },
            ].map((s) => (
              <div key={s.title} data-testid={`trust-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                className="bg-white/[0.03] border border-white/10 p-5 hover:border-gold/40 transition-colors">
                <s.icon size={20} className="text-gold mb-3" strokeWidth={1.5} />
                <div className="text-white font-medium text-sm mb-1">{s.title}</div>
                <div className="text-gray-400 text-xs leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-28 max-w-7xl mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden bg-gradient-to-br from-navy-700 to-navy-800 border border-gold/20 p-12 md:p-20 text-center grain">
          <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mb-5">Ready to grow your mission?</h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-10">Tell us where you're headed. Top Foster will map the plan, we'll build the website you're proud of, and we'll stay with you as you grow.</p>
          <Link to="/start" data-testid="cta-start-btn" className="inline-flex items-center gap-2 bg-gold text-navy px-10 py-4 font-semibold hover:bg-gold-light transition-colors">
            Start with Top Foster <ArrowRight size={18} />
          </Link>
          <div className="mt-6">
            <Link to="/grow" data-testid="cta-grow-link" className="inline-flex items-center gap-2 text-gold text-sm font-medium hover:gap-3 transition-all">
              See how we help you grow after launch <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function SectionHead({ label, title, center = false }) {
  return (
    <div className={center ? "text-center" : ""}>
      <div className={`flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4 ${center ? "justify-center" : ""}`}>
        {!center && <span className="h-px w-8 bg-gold" />} {label}
      </div>
      <h2 className={`font-heading text-3xl sm:text-4xl text-white tracking-tight max-w-2xl ${center ? "mx-auto text-center" : ""}`}>{title}</h2>
    </div>
  );
}

const FAQS = [
  {
    q: "What is ATF WebWorks?",
    a: "ATF WebWorks is an AI-powered business growth platform. We go beyond building websites — we help you plan, build, launch, and grow your business with AI-powered strategy, a custom website, and ongoing support. It starts with Top Foster understanding your business, then recommends and builds the right solution, and stays with you as you grow.",
  },
  {
    q: "How does Top Foster work?",
    a: "Top Foster is your AI Business Strategist. You answer a few simple questions, Top Foster builds your Business Profile, generates a growth roadmap, and guides what we build and improve over time. AI drafts and recommends; our team reviews and delivers — so you get speed without losing the human touch.",
  },
  {
    q: "What is my Business Profile?",
    a: "Your Business Profile is what we build about your business — your mission, audience, goals, and what makes you different. You teach it once, and it's reused everywhere: your website, content, proposals, and growth plan. The more complete it is, the smarter and faster everything we create for you becomes.",
  },
  {
    q: "What is Top Foster?",
    a: "Top Foster is our AI that can draft content, suggest SEO improvements, and point you toward sensible next steps. It's a helpful tool that speeds up the work — but it's not the whole story. Everything Top Foster produces is reviewed by our team, so technology always stays in service of your people and your mission.",
  },
  {
    q: "Do you only build websites, or do you help after launch?",
    a: "We don't disappear at launch. Your website is the beginning — with Hosting & Care from $49/mo we keep it secure, current, and growing, and switch on AI capabilities like lead intake as you're ready.",
  },
];

function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-28 max-w-3xl mx-auto px-6 md:px-12 scroll-mt-24" data-testid="faq-section">
      <SectionHead label="Questions?" title="Frequently asked" center />
      <div className="mt-14 space-y-3">
        {FAQS.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="glass border border-white/10 overflow-hidden" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-q-${i}`}
                className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-white/[0.03] transition-colors">
                <span className="font-heading text-lg text-white">{item.q}</span>
                <ChevronDown size={18} className={`text-gold shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                  <p className="px-6 pb-6 text-gray-400 leading-relaxed" data-testid={`faq-a-${i}`}>{item.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
